import React from "react";
import { DateTime } from "luxon";
import { DispatchProp, connect } from "react-redux";
import { isEmpty } from "../../utils/lodash";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import {
    getSettingString,
    getSettingEnum,
    getSettingNumber,
    getSettingBoolean,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { TimerLocation } from "../../models/enums/TimerLocation";
import { TimerType } from "../../models/enums/TimerType";
import { TimerAction } from "../../models/enums/TimerAction";
import { DispositionCode } from "../../models/enums/DispositionCode";
import { RecalledText } from "../../containers/shared/RecalledText";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { NavigationType } from "../../models/enums/NavigationType";
import { translate } from "../../models/utilities/translationUtilities";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { setNavigationEnabled, navigationQuit } from "../../redux/actions";
import { __getStore__ } from "../../redux/store";
import { Piping } from "../../piping";
import { StoreState } from "../../models/states/StoreState";
import { selectNavigationDisabled } from "../../redux/selectors/interview/navigation";

interface StateProps {
    navigationEnabled: boolean;
}

interface Props extends ControlProps, StateProps, DispatchProp {}

interface State {
    displayText: string;
    displayMessage?: string | JSX.Element;
    totalTime: string;
    clickCount: string;
    firstClick: string;
    lastClick: string;
    navigationEnabled: boolean;
}

const mapStateToProps = (state: StoreState): StateProps => ({
    navigationEnabled: !selectNavigationDisabled(state),
});

class TimerControlImpl extends BaseControl<Props, State> {
    secondTimerId: number;
    actionTimerId: number;
    startTime: Date;
    firstClickTime: Date;
    lastClickTime: Date;
    clickCount = 0;
    moved = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            displayText: "",
            totalTime: "0",
            clickCount: "0",
            firstClick: "0",
            lastClick: "0",
            navigationEnabled: true,
        };
    }

    totalTimeVar = (props: Props) => getSettingString(props.question, "TotalTimeVariableName");
    firstClickVar = (props: Props) => getSettingString(props.question, "FirstClickVariableName");
    lastClickVar = (props: Props) => getSettingString(props.question, "LastClickVariableName");
    clickCountVar = (props: Props) => getSettingString(props.question, "ClickCountVariableName");
    dateFormat = (props: Props) => getSettingString(props.question, "TimeFormat", "hh:mm:ss");
    secondToAction = (props: Props) => getSettingNumber(props.question, "SecondToAction", 0);
    timerType = (props: Props) => getSettingNumber(props.question, "Type");
    timerAction = (props: Props) => getSettingNumber(props.question, "Action");
    actionMessage = (props: Props) => translate(props.question, "ActionMessage", props.language);
    terminateSurveyActionUrl = (props: Props) =>
        translate(props.question, "TerminateSurveyActionURL", props.language);

    terminateSurveyActionDisposition = (props: Props) => {
        return getSettingEnum(props.question, DispositionCode, "TerminateSurveyActionDisposition");
    };

    terminateSurveyActionMessage = (props: Props) => {
        const message = translate(props.question, "TerminateSurveyActionMessage", props.language);
        if (!isEmpty(message)) {
            const { interview } = __getStore__().getState();
            return Piping.evaluateRecalls(interview, message);
        }
        return null;
    };

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        window.addEventListener("click", this.onWindowClick, true);
        this.moved = false;
        this.startTime = new Date();
        this.onSecondTimer();
        if (this.secondToAction(props) > 0)
            this.actionTimerId = window.setTimeout(
                this.onActionTimeout,
                this.secondToAction(props) * 1000,
            );
        this.secondTimerId = window.setInterval(this.onSecondTimer, 1000);
        if (this.timerAction(props) === TimerAction.EnableNavigation) {
            this.setState({ navigationEnabled: false });
            props.dispatch(setNavigationEnabled(false));
        } else {
            this.setState({ navigationEnabled: true });
        }
    }

    componentDidUpdate() {
        if (this.props.navigationEnabled && !this.state.navigationEnabled) {
            this.props.dispatch(setNavigationEnabled(false));
        }
    }

    finalizeQuestion(question: QuestionModel, props: Props) {
        super.finalizeQuestion(question, props);
        window.removeEventListener("click", this.onWindowClick, true);
        window.clearTimeout(this.actionTimerId);
        window.clearInterval(this.secondTimerId);
        if (this.timerAction(props) === TimerAction.EnableNavigation)
            props.dispatch(setNavigationEnabled(true));
    }

    setTimerVariables = (props: Props) => {
        this.setMentions(this.props, this.totalTimeVar(this.props), (mentions) =>
            mentions.set(this.state.totalTime),
        );
        this.setMentions(this.props, this.clickCountVar(this.props), (mentions) =>
            mentions.set(this.state.clickCount),
        );
        this.setMentions(this.props, this.firstClickVar(this.props), (mentions) =>
            mentions.set(this.state.firstClick),
        );
        this.setMentions(this.props, this.lastClickVar(this.props), (mentions) =>
            mentions.set(this.state.lastClick),
        );
    };

    onWindowClick = (event: any) => {
        const navBar = document.getElementsByClassName("navigationBar")[0];
        if (navBar != null && navBar.contains(event.target)) return; // Prevent clicks on navigation bar buttons from registering as a page click.
        if (this.firstClickTime === undefined) this.firstClickTime = new Date();
        this.lastClickTime = new Date();
        this.clickCount++;
        this.setState((s, p) => {
            const newState = { ...s };
            newState.clickCount = this.clicks();
            newState.firstClick = this.firstClick();
            newState.lastClick = this.lastClick();
            return newState;
        });
    };

    onSecondTimer = () => {
        this.setState((s, p) => {
            const newState = { ...s };
            newState.totalTime = this.totalTime();
            newState.firstClick = this.firstClick();
            newState.lastClick = this.lastClick();
            return newState;
        });
        this.updateDisplay();
    };

    onActionTimeout = () => {
        switch (this.timerAction(this.props)) {
            case TimerAction.AutoAdvance: {
                if (this.props.onNavigation) this.props.onNavigation(NavigationType.Next);
                window.setTimeout(window.clearInterval, 200, this.secondTimerId);
                break;
            }
            case TimerAction.DisplayMessage: {
                this.setState({
                    displayMessage: <RecalledText text={this.actionMessage(this.props)} />,
                });
                break;
            }
            case TimerAction.EnableNavigation: {
                this.setState({ navigationEnabled: true }, () =>
                    this.props.dispatch(setNavigationEnabled(true)),
                );
                break;
            }
            case TimerAction.TerminateSurvey: {
                const { id, settings } = this.props.question;
                const disposition = this.terminateSurveyActionDisposition(this.props);
                const redirectUrl = this.terminateSurveyActionUrl(this.props);
                const message = this.terminateSurveyActionMessage(this.props);
                const terminationSurveyAction = Number(settings.TerminateSurveyAction);

                this.props.dispatch(
                    navigationQuit(disposition, {
                        forcedQuit: true,
                        questionId: id,
                        redirectUrl,
                        message,
                        terminationSurveyAction,
                    }),
                );
                break;
            }
            default:
                break;
        }
    };

    totalTime = () => this.dateDiffInSeconds([new Date(), this.startTime]);

    firstClick = () => this.dateDiffInSeconds([this.firstClickTime, this.startTime]);

    lastClick = () => this.dateDiffInSeconds([this.lastClickTime, this.startTime]);

    dateDiffInSeconds = (dates: Date[]) => {
        const [a, b] = dates.map((date) => DateTime.fromJSDate(date));
        return String(Math.trunc(a.diff(b, "seconds").seconds));
    };

    clicks = () => String(this.clickCount || "");

    updateDisplay() {
        const start = DateTime.fromJSDate(this.startTime);
        const now = DateTime.fromJSDate(new Date());
        const diff = now.diff(start);
        const dateFormat = this.dateFormat(this.props).replace("hh", "HH");
        this.setState((s, p) => {
            const newState = { ...s };
            if (this.timerType(this.props) === TimerType.CountDown) {
                const timeToAction = Math.max(
                    this.secondToAction(this.props) * 1000 - diff.milliseconds,
                    0,
                );
                newState.displayText = DateTime.fromMillis(timeToAction)
                    .toUTC()
                    .toFormat(dateFormat);
            } else {
                newState.displayText = DateTime.fromMillis(diff.milliseconds)
                    .toUTC()
                    .toFormat(dateFormat);
            }
            return newState;
        });
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        this.setTimerVariables(this.props);
        return new ValidationBuilder().build();
    }

    renderText(question: QuestionModel) {
        if (!hasSetting(question, "Show") || !getSettingBoolean(question, "Show")) return null;
        return (
            <>
                <div className="timerLabel">
                    <RecalledText text={translate(question, "Label", this.props.language)} />
                </div>
                <div className="timerTime">{this.state.displayText}</div>
            </>
        );
    }

    renderQuestion(question: QuestionModel): JSX.Element | null {
        const align = getSettingNumber(question, "Location", TimerLocation.Left);
        let textAlign: "center" | "left" | "right" = "center";
        if (align === TimerLocation.Left) textAlign = "left";
        if (align === TimerLocation.Right) textAlign = "right";
        return (
            <div className="timerDisplay" style={{ textAlign }}>
                {this.renderText(question)}
                <div className="timerMessage">{this.state.displayMessage}</div>
            </div>
        );
    }
}

export const TimerControl = connect(mapStateToProps)(TimerControlImpl);
